// Contatos contra o WootDash — /contatos. CRUD real (criar/editar), mas sem exclusão
// (não existe endpoint — deletar um contato que já tem conversa/mensagem vinculada
// quebraria a FK, não é uma ação trivial) e sem gestão de etiqueta por contato ainda
// (ContactTag existe no domínio, mas só é lido pelo filtro de busca — não há endpoint
// de vincular/desvincular). Também sem histórico de conversa por contato (Fase 3).
import { WOOTDASH_BASE, wootdashAuthHeaders } from '@/api/wootdash'
import type { CpContact } from './wootdashTemplates'

export type { CpContact }

function authHeaders(token: string, json = false) {
  return { ...wootdashAuthHeaders(token), ...(json ? { 'Content-Type': 'application/json' } : {}) }
}

export async function searchContacts(token: string, search: string, page: number, pageSize: number): Promise<{ contacts: CpContact[]; total: number }> {
  try {
    const params = new URLSearchParams({ pagina: String(page), tamanhoPagina: String(pageSize) })
    if (search) params.set('busca', search)
    const res = await fetch(`${WOOTDASH_BASE}/contatos?${params}`, { headers: authHeaders(token) })
    if (!res.ok) return { contacts: [], total: 0 }
    const data = await res.json()
    return { contacts: data.contacts ?? [], total: data.total ?? 0 }
  } catch {
    return { contacts: [], total: 0 }
  }
}

export interface CpContactDetail {
  id: string
  name: string
  phone: string | null
  email: string | null
  avatarUrl: string | null
  isBlocked: boolean
  blockedReason: string | null
}

export async function getContact(token: string, id: string): Promise<CpContactDetail | null> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/contatos/${id}`, { headers: authHeaders(token) })
    if (!res.ok) return null
    return await res.json()
  } catch {
    return null
  }
}

export interface ContactMutationResult { ok: boolean; contact?: CpContactDetail }

export async function createContact(token: string, name: string, phone: string, email: string): Promise<ContactMutationResult> {
  const res = await fetch(`${WOOTDASH_BASE}/contatos`, {
    method: 'POST', headers: authHeaders(token, true), body: JSON.stringify({ name, phone: phone || null, email: email || null }),
  })
  if (!res.ok) return { ok: false }
  return { ok: true, contact: await res.json() }
}

export async function updateContact(token: string, id: string, name: string, phone: string, email: string): Promise<ContactMutationResult> {
  const res = await fetch(`${WOOTDASH_BASE}/contatos/${id}`, {
    method: 'PUT', headers: authHeaders(token, true), body: JSON.stringify({ name, phone: phone || null, email: email || null }),
  })
  if (!res.ok) return { ok: false }
  return { ok: true, contact: await res.json() }
}

export async function updateContactAvatar(token: string, id: string, avatarUrl: string): Promise<boolean> {
  const res = await fetch(`${WOOTDASH_BASE}/contatos/${id}/avatar`, {
    method: 'PATCH', headers: authHeaders(token, true), body: JSON.stringify({ avatarUrl: avatarUrl || null }),
  })
  return res.ok
}

// ── Tela de Contatos (lista, ficha, etiquetas, exclusão, importação) ──────────────────────

export interface TagChip { id: string; name: string; color: string }

export interface ContactRow {
  id: string
  name: string
  phone: string | null
  email: string | null
  avatarUrl: string | null
  company: string | null
  isBlocked: boolean
  lastActivityAt: string | null
  conversationCount: number
  tags: TagChip[]
}

export interface ContactProfile extends ContactRow {
  whatsappId: string | null
  city: string | null
  notes: string | null
  socialProfiles: Record<string, string>
  customFields: Record<string, string>
  blockedReason: string | null
  createdAt: string
}

export interface ContactProfileForm {
  name: string
  phone: string
  email: string
  company: string
  city: string
  notes: string
  socialProfiles: Record<string, string>
}

export const SOCIAL_KEYS = ['instagram', 'facebook', 'linkedin', 'tiktok', 'twitter', 'site'] as const

async function problemOf(res: Response, fallback: string): Promise<string> {
  const data = await res.json().catch(() => undefined)
  return data?.problem || fallback
}

export async function listContactsPanel(
  token: string, opts: { search?: string; tagId?: string; page: number; pageSize: number }, signal?: AbortSignal,
): Promise<{ contacts: ContactRow[]; total: number }> {
  const params = new URLSearchParams({ pagina: String(opts.page), tamanhoPagina: String(opts.pageSize) })
  if (opts.search) params.set('busca', opts.search)
  if (opts.tagId) params.set('etiquetaId', opts.tagId)
  const res = await fetch(`${WOOTDASH_BASE}/contatos/painel?${params}`, { headers: authHeaders(token), signal })
  if (!res.ok) throw new Error('Não foi possível carregar os contatos.')
  const data = await res.json()
  return { contacts: data.contacts ?? [], total: data.total ?? 0 }
}

export async function getContactProfile(token: string, id: string): Promise<ContactProfile | null> {
  const res = await fetch(`${WOOTDASH_BASE}/contatos/${id}/ficha`, { headers: authHeaders(token) })
  return res.ok ? await res.json() : null
}

export async function saveContactProfile(token: string, id: string | null, form: ContactProfileForm): Promise<ContactProfile> {
  const res = await fetch(`${WOOTDASH_BASE}/contatos/${id ? `${id}/ficha` : 'ficha'}`, {
    method: id ? 'PUT' : 'POST', headers: authHeaders(token, true),
    body: JSON.stringify({
      name: form.name, phone: form.phone || null, email: form.email || null,
      company: form.company || null, city: form.city || null, notes: form.notes || null, socialProfiles: form.socialProfiles,
    }),
  })
  if (!res.ok) throw new Error(await problemOf(res, 'Não foi possível salvar o contato.'))
  return await res.json()
}

export async function setContactTags(token: string, id: string, tagIds: string[]): Promise<TagChip[]> {
  const res = await fetch(`${WOOTDASH_BASE}/contatos/${id}/etiquetas`, {
    method: 'PUT', headers: authHeaders(token, true), body: JSON.stringify({ etiquetaIds: tagIds }),
  })
  if (!res.ok) throw new Error('Não foi possível salvar as etiquetas.')
  return await res.json()
}

/** ok=false com conversationCount quando o contato já conversou (não dá pra excluir — só bloquear). */
export async function deleteContact(token: string, id: string): Promise<{ ok: boolean; conversationCount?: number }> {
  const res = await fetch(`${WOOTDASH_BASE}/contatos/${id}`, { method: 'DELETE', headers: authHeaders(token) })
  if (res.ok) return { ok: true }
  if (res.status === 409) {
    const data = await res.json().catch(() => undefined)
    return { ok: false, conversationCount: data?.conversationCount ?? 0 }
  }
  throw new Error(res.status === 403 ? 'Você não tem permissão para excluir contatos.' : 'Não foi possível excluir o contato.')
}

export async function findContactConversationsByText(token: string, id: string, text: string): Promise<string[]> {
  const res = await fetch(`${WOOTDASH_BASE}/contatos/${id}/conversas-com-texto?busca=${encodeURIComponent(text)}`, { headers: authHeaders(token) })
  return res.ok ? await res.json() : []
}

export interface ImportCsvResult { created: number; duplicates: number; problems: { line: number; reason: string }[] }

export async function importContactsCsv(token: string, file: File): Promise<ImportCsvResult> {
  const body = new FormData()
  body.append('arquivo', file)
  const res = await fetch(`${WOOTDASH_BASE}/contatos/importar`, { method: 'POST', headers: wootdashAuthHeaders(token), body })
  if (!res.ok) throw new Error(await problemOf(res, 'Não foi possível importar o arquivo.'))
  return await res.json()
}
